import React, { useCallback, useMemo, useState } from 'react'
import Router from 'next/router'
import styled from 'styled-components'
import * as R from 'ramda'
import PointBalance from './PointBalance'
import RedeemSKUButton from './RedeemSKUButton'
import Tooltip from 'components/UI/TooltipV2'
import { bindPhoneThenReload } from 'utils/sdkUtil'
import { formatNumerics } from 'utils/formatUtil'
import { loginOrGetToken } from 'utils/loginUtil'
import { mobileSize } from 'theme'
import { openLinkBlank } from 'utils/appSchemeUtil'
import { useTrackingStore } from 'store/trackingStore'
import { COUPON_REDEEMABLE_STATUS } from '/constants/coupon'
import { COUPON_SCREEN_NAME, EVENT_ACTION, BUTTON_NAME } from 'tracking'
import { useUserAgentStore } from 'store/userAgentStore'
import AlertDialog from 'components/UI/AlertDialog'

const LINK_TO_HOW_TO_GET_POINTS = encodeURI(
  'https://www.hk01.com/獎賞專區/371302/香港01會員獎賞計劃'
)

const SkuButton = ({
  isLoggedIn,
  hasBindPhone,
  isFetchingProfile,
  pointsValue,
  redeemQuota,
  redeemProfile,
  redeemCoupon,
  hasRedeemed,
  skuData,
  fastShare,
  isMobileBreakpoint,
}) => {
  const [confirmTooltip, setConfirmTooltip] = useState(false)
  const [openTooltip, setOpenTooltip] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const { trackEvent } = useTrackingStore()
  const { userRemainQuota, redeemableStatus, userPoints } = redeemProfile
  const isFree = pointsValue <= 0
  const takeOrRedeemText = isFree ? '領取' : '兌換'
  const userAgentStore = useUserAgentStore()
  const { isPWA } = userAgentStore

  const onLoginToRedeemCoupon = useCallback(() => {
    trackEvent(EVENT_ACTION.CLICK, {
      screen: COUPON_SCREEN_NAME.SKU,
      button: BUTTON_NAME.LOGIN,
    })
    const loginParams = { pathname: window.location.pathname }
    // TODO: refactor to util and make it testable
    switch (true) {
      case fastShare && isPWA():
        loginParams.loginSource = 'fastShare-PWA-sku'
        break
      case !!fastShare:
        loginParams.loginSource = 'fastShare-sku'
        break
      case isPWA():
        loginParams.loginSource = 'PWA-sku'
        break
    }

    return loginOrGetToken(loginParams)
  }, [fastShare, isPWA, trackEvent])
  const closeTooltip = useCallback(() => setOpenTooltip(false), [])
  const onBindPhoneToRedeemCoupon = useCallback(async () => {
    trackEvent(EVENT_ACTION.CLICK, {
      screen: COUPON_SCREEN_NAME.SKU,
      button: BUTTON_NAME.BIND_PHONE,
    })
    // TODO: refactor to util and make it testable
    const trackSource = () => {
      let trackSourceValue
      switch (true) {
        case fastShare && isPWA():
          trackSourceValue = 'fastShare-PWA-sku'
          break
        case !!fastShare:
          trackSourceValue = 'fastShare-sku'
          break
        case isPWA():
          trackSourceValue = 'PWA-sku'
          break
      }
      return trackSourceValue
    }
    await bindPhoneThenReload(trackSource())
  }, [fastShare, isPWA, trackEvent])
  const { stat: stockInfo } = skuData
  const { availableNum } = stockInfo

  const canRedeem = redeemableStatus === COUPON_REDEEMABLE_STATUS.REDEEM_OK

  const notEnoughPoints =
    redeemableStatus === COUPON_REDEEMABLE_STATUS.INSUFFICIENT_POINTS
  const showRemainingQuota = !R.isNil(userRemainQuota) && canRedeem
  const {
    tooltipText,
    onClickTooltip,
    customColor,
    showShadow,
    enableBackdropClick,
  } = useMemo(() => {
    const showRemainingQuota = canRedeem && hasRedeemed
    if (showRemainingQuota) {
      return {
        customColor: '#252525',
        tooltipText: `已${takeOrRedeemText}${redeemQuota - userRemainQuota}次`,
      }
    }

    if (!showRemainingQuota && redeemProfile.userRemainQuota === 0) {
      return {
        tooltipText: '查看我的優惠券',
        onClickTooltip: () => {
          Router.push('/mycoupon')
        },
      }
    }

    if (notEnoughPoints) {
      if (!isFetchingProfile) {
        setOpenTooltip(true)
      }
      return {
        tooltipText: (
          <InsufficientPointsLabel>
            積分不足? 查看如何賺取積分﹥
          </InsufficientPointsLabel>
        ),
        onClickTooltip: () => {
          openLinkBlank(LINK_TO_HOW_TO_GET_POINTS)
        },
      }
    }

    if (confirmTooltip) {
      return {
        tooltipText: (
          <ConfirmRedeemTooltips
            redeemCoupon={redeemCoupon}
            pointsValue={pointsValue}
            setConfirmTooltip={setConfirmTooltip}
            setOpenTooltip={setOpenTooltip}
            skuData={skuData}
            trackEvent={trackEvent}
          />
        ),
        customColor: '#ffffff',
        showShadow: true,
        enableBackdropClick: true,
      }
    }

    return {}
  }, [
    canRedeem,
    hasRedeemed,
    redeemProfile.userRemainQuota,
    notEnoughPoints,
    confirmTooltip,
    takeOrRedeemText,
    redeemQuota,
    userRemainQuota,
    isFetchingProfile,
    redeemCoupon,
    pointsValue,
    skuData,
    trackEvent,
  ])
  const disableRedeemButton =
    availableNum === 0 ||
    (!canRedeem && isLoggedIn && hasBindPhone) ||
    isFetchingProfile

  const handleClickRedeem = useCallback(() => {
    if (isFree) {
      return redeemCoupon()
    }
    if (isMobileBreakpoint) {
      setModalVisible(true)
    } else {
      setConfirmTooltip(true)
      setOpenTooltip(true)
    }
  }, [isFree, isMobileBreakpoint, redeemCoupon])

  const closeConfirmModal = useCallback(() => setModalVisible(false), [])

  const onClickButton = !isLoggedIn
    ? onLoginToRedeemCoupon
    : !hasBindPhone
    ? onBindPhoneToRedeemCoupon
    : handleClickRedeem

  return (
    <>
      <RedeemButtonWapper>
        <Tooltip
          title={
            <TooltipTitleWrapper
              {...(onClickTooltip && {
                onClick: onClickTooltip,
              })}
            >
              {tooltipText}
            </TooltipTitleWrapper>
          }
          placement="bottom"
          arrow
          customColor={customColor}
          showShadow={showShadow}
          open={openTooltip}
          onClose={closeTooltip}
          enableBackdropClick={enableBackdropClick}
        >
          <RedeemSKUButton
            disabled={disableRedeemButton || openTooltip}
            {...(!disableRedeemButton && {
              onClick: onClickButton,
            })}
            isFetchingProfile={isFetchingProfile}
          >
            <RedeemButtonText
              redeemQuota={redeemQuota}
              takeOrRedeemText={takeOrRedeemText}
              status={redeemableStatus}
              isLoggedIn={isLoggedIn}
              hasBindPhone={hasBindPhone}
              isFree={isFree}
              pointsValue={pointsValue}
              showRemainingQuota={showRemainingQuota}
              userRemainQuota={userRemainQuota}
              availableNum={availableNum}
            />
          </RedeemSKUButton>
        </Tooltip>
        <PointBalance
          points={formatNumerics(userPoints)}
          isFetchingProfile={isFetchingProfile}
          isLoggedIn={isLoggedIn}
          hasBindPhone={hasBindPhone}
        />
      </RedeemButtonWapper>
      <AlertDialog
        open={modalVisible}
        handleClose={closeConfirmModal}
        title={'兌換優惠券'}
        description={`確定要以${pointsValue}積分兌換優惠劵？`}
        subButtonText={'取消'}
        buttonText={'確定'}
        subButtonClick={closeConfirmModal}
        buttonClick={redeemCoupon}
      />
    </>
  )
}

const RedeemButtonText = ({
  redeemQuota,
  takeOrRedeemText,
  status,
  isLoggedIn,
  hasBindPhone,
  isFree,
  pointsValue,
  userRemainQuota,
  availableNum,
}) => {
  if (availableNum === 0) {
    return `${takeOrRedeemText}完畢`
  }
  if (!isLoggedIn) {
    return `手機號碼登入以${takeOrRedeemText}`
  }
  if (!hasBindPhone) {
    return `綁定手機以${takeOrRedeemText}`
  }
  return (
    <>
      <RedeemStatusText
        redeemQuota={redeemQuota}
        takeOrRedeemText={takeOrRedeemText}
        status={status}
        isFree={isFree}
        pointsValue={pointsValue}
      />
      {redeemQuota && (
        <LimitText>
          限{takeOrRedeemText}
          {redeemQuota}次
          {!R.isNil(userRemainQuota) &&
            userRemainQuota !== redeemQuota &&
            ` | 已${takeOrRedeemText}${redeemQuota - userRemainQuota}次`}
        </LimitText>
      )}
    </>
  )
}

const RedeemStatusText = ({ takeOrRedeemText, status, isFree, pointsValue }) =>
  ({
    OUT_OF_STOCK: `${takeOrRedeemText}完畢`,
    INSUFFICIENT_POINTS: isFree
      ? `免積分${takeOrRedeemText}`
      : `${pointsValue} 積分${takeOrRedeemText}`,
    NO_QUOTA: `已${takeOrRedeemText}`,
    REDEEM_OK: isFree
      ? `免積分${takeOrRedeemText}`
      : `${pointsValue} 積分${takeOrRedeemText}`,
    DEFAULT: '',
  }[status])

const ConfirmRedeemTooltips = ({
  redeemCoupon,
  pointsValue,
  setConfirmTooltip,
  setOpenTooltip,
  skuData,
  trackEvent,
}) => {
  const cancelRedeem = () => {
    setOpenTooltip(false)
    const st = setTimeout(() => {
      setConfirmTooltip(false)
      clearTimeout(st)
    }, 300)
    trackEvent(EVENT_ACTION.CLICK, {
      button: BUTTON_NAME.CANCEL_REDEEM,
      screen: COUPON_SCREEN_NAME.SKU,
      item_id: skuData.id,
      item_type: 'sku',
      item_status: 'available',
    })
  }
  const confirmRedeem = () => {
    trackEvent(EVENT_ACTION.CLICK, {
      button: BUTTON_NAME.CONFIRM_REDEEM,
      screen: COUPON_SCREEN_NAME.SKU,
      item_id: skuData.id,
      item_type: 'sku',
      item_status: 'available',
    })
    redeemCoupon()
    cancelRedeem()
  }
  return (
    <ConfirmContentWapper>
      <ConfirmText>確定以 {pointsValue} 積分兌換優惠券?</ConfirmText>
      <ButtonsWapper>
        <ButtonCancel onClick={cancelRedeem}>取消</ButtonCancel>
        <ButtonConfirm onClick={confirmRedeem}>確定</ButtonConfirm>
      </ButtonsWapper>
    </ConfirmContentWapper>
  )
}
const TooltipTitleWrapper = styled.div`
  user-select: none;

  /* revert disabled pointer-events of mui tooltip */
  pointer-events: auto;
`

const RedeemButtonWapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-top: 24px;
  @media ${mobileSize} {
    justify-content: space-between;
    margin-top: 0;
    box-sizing: border-box;
    padding: 8px 12px;
    border-radius: 2px;
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    background: #fff;
    flex-direction: row-reverse;
    box-shadow: 0 -2px 4px 0 rgba(0, 0, 0, 0.05);
  }
`
const LimitText = styled.p`
  font-size: 12px;
  font-weight: 300;
  padding: 0;
  margin: 0;
`
const ConfirmContentWapper = styled.div`
  padding: 8px 0;
  font-weight: 400;
`
const ConfirmText = styled.div`
  font-size: 14px;
  color: ${(p) => p.theme.color.dark200};
`
const ButtonsWapper = styled.div`
  padding-top: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  div {
    width: 78px;
    height: 30px;
    border-radius: 4px;
    font-size: 14px;
    line-height: 30px;
    text-align: center;
    cursor: pointer;
  }
`
const ButtonCancel = styled.div`
  border: 1px solid #cacfd1;
  color: ${(p) => p.theme.color.dark200};
`
const ButtonConfirm = styled.div`
  border: 1px solid ${(p) => p.theme.color.primary};
  color: #fff;
  background-color: ${(p) => p.theme.color.primary};
  margin-left: 8px;
`
const InsufficientPointsLabel = styled.div`
  cursor: pointer;
  font-size: 12px;
  font-weight: normal;
`
export default SkuButton
